import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { MARKETPLACE_OPTIONS, VenueAlias, VenueAliasGroup, VenueAliasGroupsResponse } from "./types";

type SortBy = "sfVenueName" | "sfVenueCity" | "sfVenueState";
type SortDirection = "asc" | "desc";

interface ListVenueAliasGroupsParams {
	page: number;
	pageSize: number;
	searchTerm?: string;
	sortBy?: SortBy;
	sortDirection?: SortDirection;
}

const toStringOrEmpty = (value: unknown): string => (typeof value === "string" ? value : "");

const toNumberOrNull = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
};

const normalizeMarketplace = (value: unknown): VenueAlias["marketplace"] => {
	const marketplace = String(value ?? "TICKETMASTER")
		.trim()
		.toUpperCase()
		.replace(/\s+/g, "_");
	const allowed = new Set(MARKETPLACE_OPTIONS.map((option) => option.value));
	return allowed.has(marketplace as VenueAlias["marketplace"])
		? (marketplace as VenueAlias["marketplace"])
		: "TICKETMASTER";
};

const MARKETPLACE_BY_ID: Record<number, VenueAlias["marketplace"]> = {
	104: "STUBHUB",
	105: "TICKET_NETWORK",
	106: "TICKETMASTER",
	115: "VIVID_SEATS",
	116: "TICKET_EVOLUTION",
	117: "SEATGEEK",
	118: "TICK_PICK",
	120: "VIAGOGO",
	121: "GAME_TIME",
	122: "FAN_XCHANGE",
	125: "AXS",
	126: "STUBHUB_INT",
	127: "GO_TICKETS",
};

const marketplaceFromRaw = (alias: Record<string, unknown>): VenueAlias["marketplace"] => {
	const marketplaceFromName = alias.marketplace ?? alias.marketplaceName;
	if (marketplaceFromName) {
		return normalizeMarketplace(marketplaceFromName);
	}

	const marketplaceId = toNumberOrNull(alias.marketplaceId);
	if (marketplaceId !== null && MARKETPLACE_BY_ID[marketplaceId]) {
		return MARKETPLACE_BY_ID[marketplaceId];
	}

	return "TICKETMASTER";
};

const toAlias = (alias: Record<string, unknown>): VenueAlias => {
	const aliasProps =
		typeof alias.props === "object" && alias.props !== null ? (alias.props as Record<string, unknown>) : alias;
	const idCandidate = aliasProps.venueAliasId ?? aliasProps.id ?? aliasProps.aliasId ?? aliasProps.marketplaceVenueId;
	const aliasName = toStringOrEmpty(aliasProps.marketplaceVenueName ?? aliasProps.aliasName ?? aliasProps.name);
	const marketplace = marketplaceFromRaw(aliasProps);
	return {
		id:
			typeof idCandidate === "number" || typeof idCandidate === "string"
				? idCandidate
				: `${marketplace}-${aliasName}`,
		name: aliasName,
		marketplace,
		hitCount: toNumberOrNull(aliasProps.hitCount ?? aliasProps.hits ?? aliasProps.matchCount ?? aliasProps.useCount),
	};
};

const toGroup = (item: Record<string, unknown>, idx: number): VenueAliasGroup => {
	const aliasesRaw = Array.isArray(item.aliases) ? item.aliases : [];
	const idCandidate = item.sfVenueId ?? item.id ?? item.venueGroupId ?? `group-${idx}`;
	const city = toStringOrEmpty(item.sfVenueCity ?? item.city);
	const state = toStringOrEmpty(item.sfVenueStateCode ?? item.sfVenueState ?? item.stateCode ?? item.state);

	return {
		id: typeof idCandidate === "number" || typeof idCandidate === "string" ? idCandidate : `group-${idx}`,
		sfVenueName: toStringOrEmpty(item.sfVenueName ?? item.venueName ?? item.canonicalVenueName),
		city,
		state,
		aliases: aliasesRaw
			.filter((alias): alias is Record<string, unknown> => typeof alias === "object" && alias !== null)
			.map(toAlias)
			.filter((alias) => Boolean(alias.name.trim())),
	};
};

export const venueAliasesService = {
	async listVenueAliasGroups(params: ListVenueAliasGroupsParams): Promise<VenueAliasGroupsResponse> {
		const response = await axios.get(`${getBackendUrl()}/venue-aliases`, {
			params: {
				page: params.page,
				pageSize: params.pageSize,
				...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
				...(params.sortBy ? { sortBy: params.sortBy } : {}),
				...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
			},
			headers: getAuthHeaders(),
		});

		const payload = response.data ?? {};
		const itemsRaw = Array.isArray(payload.items) ? payload.items : [];
		const items = itemsRaw
			.filter((item: unknown): item is Record<string, unknown> => typeof item === "object" && item !== null)
			.map(toGroup);

		return {
			items,
			total: typeof payload.total === "number" ? payload.total : items.length,
		};
	},

	async deleteVenueAliases(venueAliasIds: Array<number | string>): Promise<void> {
		const normalizedIds = venueAliasIds
			.map((id) => (typeof id === "number" ? id : Number(id)))
			.filter((id) => Number.isInteger(id));

		if (!normalizedIds.length) {
			throw new Error("No valid alias IDs to delete.");
		}

		await axios.delete(`${getBackendUrl()}/venue-aliases`, {
			data: { venueAliasIds: normalizedIds },
			headers: getAuthHeaders(),
		});
	},
};
